import { ResourcesChartConfig } from '../../../components/ResourcesChart';

/**
 * Memory Utilization Chart Configuration
 * 
 * Displays used and max memory over time from PLATFORM_KPI table.
 * Data fields: collection_time, used_memory, max_memory
 */
export const memoryChartConfig: ResourcesChartConfig = {
  title: 'Memory Utilization',
  apiEndpoint: '/api/resources/memory',
  csvFilename: 'memory_utilization',
  chartImageFilename: 'memory_utilization_chart',
  timestampField: 'collection_time',  // PLATFORM_KPI uses collection_time
  series: [
    {
      name: 'Used Memory',
      type: 'line',  // Changed to line chart
      yAxisIndex: 0,
      color: 'info',  // --chart-7 (sky blue) - for current usage metrics
      dataKey: 'used_memory',
      dataTransform: (value: number) => value / 1e6  // Divide by 1,000,000
    },
    {
      name: 'Max Memory',
      type: 'line',  // Changed to line chart
      yAxisIndex: 0,
      color: 'chart1',  // --chart-1 (dark teal) - for capacity/limit metrics
      dataKey: 'max_memory',
      dataTransform: (value: number) => value / 1e6  // Divide by 1,000,000
    }
  ],
  yAxes: [
    {
      name: 'Memory (MB)',
      position: 'left',
      nameGap: 70,  // Increased for rotated vertical label
      formatter: '{value}'
    },
    {
      name: '',
      position: 'right',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'Used Memory (MB)',
      dataKey: 'used_memory',
      dataTransform: (value: number) => value / 1e6  // Divide by 1,000,000
    },
    {
      header: 'Max Memory (MB)',
      dataKey: 'max_memory',
      dataTransform: (value: number) => value / 1e6  // Divide by 1,000,000
    }
  ]
};

/**
 * CPU Utilization Chart Configuration
 * 
 * Displays CPU utilization percentage over time from PLATFORM_KPI table.
 * Data fields: collection_time, cpu_utilization
 */
export const cpuChartConfig: ResourcesChartConfig = {
  title: 'CPU Load',
  apiEndpoint: '/api/resources/cpu',
  csvFilename: 'cpu_utilization',
  chartImageFilename: 'cpu_utilization_chart',
  timestampField: 'collection_time',  // PLATFORM_KPI uses collection_time
  series: [
    {
      name: 'CPU Load',
      type: 'line',
      yAxisIndex: 0,
      color: 'info',  // --chart-7 (sky blue) - for utilization/percentage metrics
      dataKey: 'cpu_utilization',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'CPU Load %',
      position: 'left',
      min: 0,
      autoMax: {
        floor: 100,
        step: 50,
      },
      nameGap: 70,  // Increased for rotated vertical label
      formatter: '{value}%'
    },
    {
      name: '',
      position: 'right',
      nameGap: 50,
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'CPU Load (%)',
      dataKey: 'cpu_utilization',
      isPercentage: true
    }
  ]
};

/**
 * TEMPLATE: Add new ResourcesP charts here
 * 
 * Copy this template and modify for your chart.
 * Use standardized colors from chartColors.ts for consistency:
 * - 'success'  (--chart-positive, green)  - for utilization, performance, success metrics
 * - 'info'     (--chart-7, sky blue)       - for current values, rates, percentages
 * - 'chart1'   (--chart-1, dark teal)      - for capacity, limits, attempts
 * - 'error'    (--chart-negative, red)     - for errors, failures, critical alerts
 * - 'neutral'  (--chart-neutral, gray)     - for neutral/baseline metrics
 * - 'chart2'–'chart10' for sequential series (switchable in dark mode)
 * 
 * export const myChartConfig: ResourcesChartConfig = {
 *   title: 'My Chart Title',
 *   apiEndpoint: '/charts/my-chart',
 *   csvFilename: 'my_chart_data',
 *   chartImageFilename: 'my_chart',
 *   timestampField: 'collection_time',  // or 'timestamp' depending on API
 *   series: [
 *     {
 *       name: 'Series 1',
 *       type: 'line',  // or 'bar'
 *       yAxisIndex: 0,
 *       color: '#0EA5E9',  // Use ChartColors values (see above)
 *       dataKey: 'field_name_from_api',
 *       isPercentage: false  // optional
 *     }
 *   ],
 *   yAxes: [
 *     {
 *       name: 'Y-Axis Label',
 *       position: 'left',
 *       min: 0,  // optional
 *       max: 100,  // optional
 *       nameGap: 60,
 *       formatter: '{value}'
 *     },
 *     {
 *       name: '',
 *       position: 'right',
 *       formatter: ''
 *     }
 *   ],
 *   tableColumns: [
 *     {
 *       header: 'Column Header',
 *       dataKey: 'field_name_from_api',
 *       isPercentage: false  // optional
 *     }
 *   ]
 * };
 */
